import Tooltip from '@material-ui/core/Tooltip';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { isAllowEdit } from 'features/supplier/utils';

import NoData from '@components/NoData';
import { toastError } from '@components/Toast';

import { supplierTypeWiseFieldParams } from '@features/filter/static/filterUtil';
import AddAccessoriesParameter from '@features/supplier/component/AddAccessoriesParameter';
import AddFabricParameter from '@features/supplier/component/AddFabricParameter';
import AddGarmentParameter from '@features/supplier/component/AddGarmentParameter';
import AddTrimsParameter from '@features/supplier/component/AddTrimsParameter';
import KeyProductFabric from '@features/supplier/component/KeyProductFabric';
import KeyProductGarments from '@features/supplier/component/KeyProductGarments';
import KeyProductTrimsAndAccessories from '@features/supplier/component/KeyProductTrimsAndAccessories';
import Product from '@features/supplier/component/Product';
import SupplierTypeWiseMaterialList from '@features/supplier/component/SupplierTypeWiseMaterialList';
import ViewAllPopup from '@features/supplier/component/ViewAllPopup';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { fetchSupplierTypeWiseList } from '@store/actions/SupplierAction';

import { fetchSupplierData } from '@services/supplierService';

import ApiClient from '@lib/apiClient';

import { capitalizeFirstLetter, checkSingular, isPageReachBottom } from '@utils/Util';

const SUPPLIER_TYPE_LIST = ['GARMENTS', 'FABRIC', 'TRIMS', 'ACCESSORIES'];

const Garments = ({ supplierInfo, requestFrom }) => {
	const [parameterType, setParameterType] = useState(null);
	const [supplierList, setSupplierList] = useState([]);
	const [isAdd, setIsAdd] = useState(null);
	const [, setDefaultViewType] = useState(null);
	const [productResponse, setProductResponse] = useState({});
	const [isNitexProduct, setIsNitexProduct] = useState(null);
	const [viewAll, setViewAll] = useState(false);
	const [pageNumber, setPageNumber] = useState(0);
	const [supplierType, setSupplierType] = useState(supplierInfo?.supplierTypeList[0]);
	const [tagList, setTagList] = useState([]);
	const { id } = useParams();
	const myStateRef = useRef({});
	const dispatch = useDispatch();
	const supplierStore = useSelector((store) => store.supplierStore);
	const filterFields = useSelector((store) => store.filterFieldStore);
	const [selectedProduct, setSelectedProduct] = useState(null);

	useEffect(() => {
		setSupplierType(supplierInfo?.supplierTypeList[0]);
		const filteredData = supplierInfo?.supplierTypeList?.filter((item) =>
			SUPPLIER_TYPE_LIST.includes(item)
		);
		setSupplierList(filteredData);
	}, [supplierInfo]);

	const setMyState = (data) => {
		myStateRef.current = data;
	};

	const generateParams = (params) => {
		if (isNitexProduct !== null) {
			params += `&isNitexProduct=${isNitexProduct}`;
		}
		return params;
	};

	const fetchGarments = (page = pageNumber) => {
		dispatch(openLoader());
		let params = `?supplierId=${id}&page=${page}&size=12&sort=id,desc`;
		ApiClient.GET('fetchGarments', generateParams(params))
			.then(({ data }) => {
				if (data.currentPage > 0) {
					data.data = [...myStateRef.current.data, ...data.data];
				}
				setMyState(data);
				setProductResponse(data);
				dispatch(closeLoader());
				setIsAdd(null);
			})
			.catch((error) => {
				dispatch(closeLoader());
				toastError(error.response.data.message);
			});
	};

	useEffect(() => {
		let userInfo = localStorage.getItem('userInfo');
		setDefaultViewType(userInfo.systemPreference?.collectionDetailsViewType);
		document.addEventListener('scroll', handleScroll);
		return () => {
			document.removeEventListener('scroll', handleScroll);
		};
	}, []);

	useEffect(() => {
		if (supplierInfo?.supplierTypeList?.length > 0) {
			if (supplierType === 'GARMENTS') {
				fetchGarments();
			}
			onFetchProductTag();
		}
	}, [pageNumber, supplierType, isNitexProduct]);

	const onFetchProductTag = async () => {
		const response = await fetchSupplierData({
			supplierId: id,
			tagType: supplierType,
			type: 'PRODUCT_TAG'
		});
		setTagList(response);
	};

	const handleScroll = () => {
		if (isPageReachBottom()) {
			let { totalElements, currentPage, data } = myStateRef.current;
			if (totalElements > 0 && totalElements !== data.length) {
				setPageNumber(currentPage + 1);
			}
		}
	};

	const productsToRender = viewAll ? productResponse?.data : (productResponse?.data || []).slice(0, 8);
	const renderTagList = (list, title) => {
		return (
			<div className="categories">
				{list?.length > 0 &&
					list?.map((item) => (
						<Tooltip
							title={<span className="regular-14 ">{title}</span>}
							placement="top"
							arrow
							key={item?.id}
						>
							<span className="status cyan-1 radius regular-12 mr-2 cursor-pointer">
								{title === 'Weight range' ? item?.name : capitalizeFirstLetter(item?.name)}
							</span>
						</Tooltip>
					))}
			</div>
		);
	};

	const renderTypeWiseTag = () => {
		const {
			productCategoryResponseList,
			minGsm,
			maxGsm,
			constructionList,
			fiberList,
			colorTypeList,
			trimsCategoryList,
			brandingCategoryList
		} = tagList;
		switch (supplierType) {
			case 'GARMENTS':
				return renderTagList(productCategoryResponseList, 'Product Category');
			case 'FABRIC':
				return [
					renderTagList(constructionList, 'Category'),
					renderTagList(fiberList, 'Fiber'),
					renderTagList(colorTypeList, 'Color type'),
					minGsm && maxGsm && renderTagList([{ name: `${minGsm} - ${maxGsm} GSM` }], 'Weight range')
				];
			case 'TRIMS':
				return renderTagList(trimsCategoryList, 'Category');
			case 'ACCESSORIES':
				return renderTagList(brandingCategoryList, 'Category');
			default:
				return null;
		}
	};

	const reFetchMaterials = async () => {
		await dispatch(
			fetchSupplierTypeWiseList(id, supplierTypeWiseFieldParams(supplierType, filterFields, 0))
		);
		setIsAdd(null);
	};

	const renderAddMaterials = () => {
		if (isAdd) {
			return (
				<>
					{supplierType === 'GARMENTS' && (
						<KeyProductGarments
							tagList={tagList?.productCategoryResponseList?.map((item) => ({
								value: item.id,
								label: item.name
							}))}
							selectedProduct={selectedProduct}
							onClose={() => {
								setSelectedProduct(null);
								setIsAdd(null);
							}}
							onSuccess={async () => {
								await fetchGarments();
								setIsAdd(null);
							}}
						/>
					)}

					{supplierType === 'FABRIC' && (
						<KeyProductFabric
							onClose={() => setIsAdd(null)}
							parameterType={supplierType}
							onSuccess={reFetchMaterials}
						/>
					)}
					{supplierType === 'TRIMS' && (
						<KeyProductTrimsAndAccessories
							onClose={() => setIsAdd(null)}
							parameterType={supplierType}
							onSuccess={reFetchMaterials}
						/>
					)}
					{supplierType === 'ACCESSORIES' && (
						<KeyProductTrimsAndAccessories
							onClose={() => setIsAdd(null)}
							parameterType={supplierType}
							onSuccess={reFetchMaterials}
						/>
					)}
				</>
			);
		}
	};

	return (
		<div>
			<div className="supplier-product-header d-flex justify-content-between">
				<div className="d-flex justify-content-start align-items-center">
					<div className="inner-tabs products-tab">
						<ul>
							{supplierList?.map((item) => (
								<li
									className={`${supplierType === item ? 'active' : ''}`}
									key={item}
									onClick={() => {
										setIsAdd(null);
										setSupplierType(item);
									}}
								>
									{capitalizeFirstLetter(item)}{' '}
								</li>
							))}
						</ul>
					</div>
					<div className="ml-12">
						<p className="gray_dark_02 regular-14">
							{supplierType === 'GARMENTS'
								? checkSingular('Style', productResponse?.totalElements)
								: checkSingular('Style', supplierStore?.response?.data?.length)}
						</p>
					</div>
				</div>

				<div className="transparent-select d-flex justify-content-between align-items-center">
					<div className="transparent-sort">
						<select
							className="border-0"
							name="isNitex"
							id="isNitex"
							onChange={(e) => setIsNitexProduct(e.target.value)}
						>
							<option value="">Select</option>
							<option value={true}>Nitex Design</option>
							<option value={false}>Buyer techpack</option>
						</select>
					</div>
					{isAllowEdit() && (
						<div className="dropdown key-dropdown">
							<button
								className="border-0 d-flex align-items-center justify-content-between bg-transparent"
								type="button"
								id="add-product"
								data-toggle="dropdown"
								aria-expanded="false"
							>
								<p className="regular-14 gray_dark_02 mr-2">Add</p>
								<img src={`${process.env.PUBLIC_URL}/images/chevron_down.svg`} alt="arrow" />
							</button>
							<ul
								className="dropdown-menu dropdown-menu-right shadow-lg"
								role="menu"
								aria-labelledby="add-product"
							>
								<li onClick={() => setParameterType(supplierType)}>
									<button className="border-0 bg-transparent">
										<span className="text-nowrap p-2">
											<img
												src={`${process.env.PUBLIC_URL}/icons/Add-round24.svg`}
												alt="add"
											/>{' '}
											Product parameters
										</span>
									</button>
								</li>
								<li
									onClick={() => setIsAdd(supplierType)}
									data-toggle="modal"
									data-target="#add-supplier-design"
								>
									<button
										className="border-0 bg-transparent"
										id="design-tool-upload-button"
									>
										<span className="text-nowrap p-2">
											<img
												src={`${process.env.PUBLIC_URL}/icons/Add-round24.svg`}
												alt="add"
											/>{' '}
											{supplierType === 'GARMENTS'
												? 'Style'
												: capitalizeFirstLetter(supplierType)}
										</span>
									</button>
								</li>
							</ul>
						</div>
					)}
					<div>
						<p onClick={() => setViewAll(true)} className="cursor-pointer regular-16 text-black">
							<span className="devided">|</span> View all
						</p>
					</div>
				</div>
			</div>
			{supplierType === 'GARMENTS' ? (
				<>
					<div className="product-category d-flex justify-content-start flex-warp">
						{renderTypeWiseTag()}
					</div>
					{productResponse?.data?.length > 0 ? (
						<div className="all-designs collection-details-design on-garments-tab">
							<div className="container-fluid px-0">
								<div className="row">
									<Product
										onEditInfo={(product) => {
											setSelectedProduct(product);
											setIsAdd(supplierType);
										}}
										requestFrom={requestFrom}
										productList={productsToRender}
										isPopupView={viewAll}
										supplierId={id}
										callback={() => fetchGarments(0)}
									/>
								</div>
							</div>
						</div>
					) : (
						<NoData message="No key products added" />
					)}
				</>
			) : (
				<>
					<div className="product-category d-flex justify-content-start flex-warp">
						{renderTypeWiseTag()}
					</div>
					{supplierType && (
						<SupplierTypeWiseMaterialList
							supplierType={supplierType}
							isAdd={isAdd}
							setIsAdd={setIsAdd}
						/>
					)}
				</>
			)}

			{viewAll && (
				<ViewAllPopup
					supplierType={supplierType}
					onClose={() => setViewAll(false)}
					productList={productsToRender}
					callback={(page) => {
						fetchGarments(page);
					}}
					productResponse={productResponse}
					renderTypeWiseTag={renderTypeWiseTag}
				/>
			)}
			{parameterType === 'GARMENTS' && (
				<AddGarmentParameter
					tagList={tagList?.productCategoryResponseList?.map((item) => ({
						value: item.id,
						label: item.name
					}))}
					onClose={() => setParameterType(false)}
					parameterType={parameterType}
					onSuccess={onFetchProductTag}
				/>
			)}

			{parameterType === 'FABRIC' && (
				<AddFabricParameter
					constructionList={tagList?.constructionList?.map((item) => ({
						value: item.id,
						label: item.name
					}))}
					fiberList={tagList?.fiberList?.map((item) => ({
						value: item.id,
						label: item.name
					}))}
					colorTypeList={tagList?.colorTypeList?.map((item) => ({
						value: item.name,
						label: item.name
					}))}
					onClose={() => setParameterType(false)}
					parameterType={parameterType}
					onSuccess={onFetchProductTag}
				/>
			)}
			{parameterType === 'TRIMS' && (
				<AddTrimsParameter
					tagList={tagList?.trimsCategoryList?.map((item) => ({
						value: item.id,
						label: item.name
					}))}
					onClose={() => setParameterType(false)}
					parameterType={parameterType}
					onSuccess={onFetchProductTag}
				/>
			)}
			{parameterType === 'ACCESSORIES' && (
				<AddAccessoriesParameter
					tagList={tagList?.brandingCategoryList?.map((item) => ({
						value: item.id,
						label: item.name
					}))}
					onClose={() => setParameterType(false)}
					parameterType={parameterType}
					onSuccess={onFetchProductTag}
				/>
			)}
			{renderAddMaterials()}
		</div>
	);
};

export default Garments;
